/**
 * @module utils
 * @private
 */

import {
  geolocation,
  profiletype,
  token,
  localUserId,
  profile_id,
  profile_category,
  currentProfile
} from "../web/store";
import { services } from "../constants";
import errors from "../web/errors/index";
import { setCookie, getCookieByName } from "./cookiesUtils";
import { Base64 } from "js-base64";

const smarttv4k = "smarttv4k";
const externalDomainSecure = "https://starzplay-prod-ssl.akamaized.net";

function formatErrorObject(e) {
  const requestError = new Error();
  const status = e?.response?.status || e?.status;
  const message = e?.response?.message || e?.response?.data?.message || e?.message;
  const providerErrorCode =
    e?.response?.providerErrorCode || e?.response?.data?.providerErrorCode || e?.providerErrorCode;
  const error_code = e?.response?.data?.error_code || e?.error_code;
  const errorData = e?.response?.data?.data || e?.data;

  requestError.status = status || 0;
  requestError.message = message || errors.genericNetworkError;
  requestError.error_code = error_code;
  requestError.providerErrorCode = providerErrorCode;

  forceLogoutBasedErrorObject(e);
  // handling for delete user profile
  deletedProfileErrorHandling(e);
  return {
    ...requestError,
    ...(errorData && { data: errorData })
  };
}

/**
 * This function is for force logout feature.
 */
function forceLogoutBasedErrorObject(e) {
  const httpCode = e.response && e.response.status;
  const httpStatusCode = e.response && e.response.data && e.response.data.error_code;
  if (httpCode === 403 || httpCode === 401) {
    setCookie("forcelogout", true);
  }
}

function deletedProfileErrorHandling(e) {
  const httpCode = e.response && e.response.status;
  const errorDescription = e.response && e.response.data && e.response.data.message;
  if (httpCode === 406 && errorDescription && errorDescription === "profile does not exist") {
    // creating a custom event and dispatching in case of deleted profile, so it can be listened to the client
    const isSelectedProfileDeletedEvent = new Event("isSelectedProfileDeletedEvent");
    document.dispatchEvent(isSelectedProfileDeletedEvent);
    currentProfile.deleteProfile();
  }
}

/**
 * Returns the guest profile from cookies
 */
export function guestProfie() {
  let guestProfile = getCookieByName("guest-profile");
  guestProfile = guestProfile && Base64.decode(guestProfile);
  guestProfile = (guestProfile && JSON.parse(guestProfile)) || {};
  return guestProfile;
}

/**
 *  this function finds the version of tizen from the user agent and check low and highend models
 */
export function getTizenVersion() {
  let userAgent = navigator.userAgent;
  if (userAgent.indexOf("Tizen 2.4.0") > -1) {
    return false;
  } else if (userAgent.indexOf("Tizen 2.4") > -1) {
    return false;
  } else if (userAgent.indexOf("Tizen 2.3") > -1) {
    return false;
  } else if (userAgent.indexOf("Tizen 2.2") > -1) {
    return false;
  } else if (userAgent.indexOf("Tizen 2.1") > -1) {
    return false;
  } else if (userAgent.indexOf("Tizen 2.0") > -1) {
    return false;
  } else if (userAgent.indexOf("Tizen 1.0") > -1) {
    return false;
  } else if (userAgent.indexOf("Tizen 2.3.1") > -1) {
    return false;
  } else if (userAgent.indexOf("Tizen 2.3.2") > -1) {
    return false;
  } else if (userAgent.indexOf("Tizen 2.2.1") > -1) {
    return false;
  } else {
    return true;
  }
}

/**
 * This function returns true if LG lowEnd models otherwise it returns true.
 */
const LG_HIGHER_VERSIONS = 4;
const LG_VERSION_5 = 5;
const LG_VERSION_7 = 7;

export function isLowEndLG() {
  if (localStorage.lgSdkVersion) {
    if (parseInt(localStorage.lgSdkVersion) < LG_HIGHER_VERSIONS) {
      return true;
    } else {
      return false;
    }
  } else {
    return false;
  }
}

export function isLGWebOS567() {
  if (localStorage.lgSdkVersion) {
    if (parseInt(localStorage.lgSdkVersion) >= LG_VERSION_5 && parseInt(localStorage.lgSdkVersion) <= LG_VERSION_7) {
      return true;
    } else {
      return false;
    }
  } else {
    return false;
  }
}

/**
 * Common helper methods for SDK as Mixin functions.
 */
export default {
  /**
   * Returns the asset types list as a String.
   * @returns {string}
   */
  getMediaAssetTypes(device) {
    if (device === "webos" || device === "vidaa" || (device === "tizen" && getTizenVersion() === true))
      return "dash_widevine_spa,dash_widevine_fp_spa,dash_widevine_tk_spa,dash_t01_series_spa,dash_t01_movie_spa,filmstrip_bs,dfxp_en_spa,dfxp_ar_spa,dfxp_fr_spa,dash_widevine_ads_spa,dash_widevine_fp_ads_spa,dash_widevine_tk_ads_spa,dash_widevine_ads_vu, dash_widevine_hevc_ads_spa";
    else
      return "hss_playready_spa,hss_t01_series_spa,hss_t01_movie_spa,filmstrip_bs,dfxp_en_spa,dfxp_ar_spa,dfxp_fr_spa";
  },

  getImageWidthQueryParams() {
    return Object.keys(this.imageWidths)
      .filter(imageType => this.imageWidths[imageType])
      .map(imageType => `&${imageType}Width=${this.imageWidths[imageType]}`)
      .join("");
  },

  async getDefaultPlatform(platform) {
    const defaultPlatform = platform || this.platformName;
    const platForm4K = await this.device.supports4K();
    return platForm4K ? smarttv4k : defaultPlatform;
  },

  /**
   * Creates a URL string for the service and passed params.
   *
   * @param category
   * @param service
   * @returns {string}
   */
  buildUrl(category, service) {
    return `${this.backendHosts[category]}${service}`;
  },

  parseQueryString(params) {
    return Object.keys(params)
      .reduce((queryParams, key) => {
        !!params[key] && queryParams.push(key + "=" + params[key]);
        return queryParams;
      }, [])
      .join("&");
  },

  parseQueryParamsString(data) {
    return Object.keys(data)
      .map(key => key + "=" + data[key])
      .join("&");
  },
  /**
   * Creates a URL string for the media services.
   * @param {*} category
   * @param {*} service
   * @param {*} id
   * @param {*} supports4K
   */
  async buildMediaUrl(category, service, id, supports4K = false, extraParams = {}) {
    const currentProfileData = await currentProfile.get.call(this);
    const parentalControl = currentProfileData && currentProfileData.parentalControl;

    const platformName = await this.getDefaultPlatform();
    const device = await this.device.getPlatform();
    const queryString = this.parseQueryString(extraParams);
    const lang = localStorage.getItem("locale") || extraParams.lang || this.lang;
    return `${this.buildUrl(category, service)}${id || ""}?platform=${platformName}${
      parentalControl ? `&parentalLevel=${parentalControl}` : ""
    }${supports4K ? `&supports4K=${supports4K}` : ""}&lang=${lang}&mediaAssetTypes=${this.getMediaAssetTypes(
      device
    )}${this.getImageWidthQueryParams()}${queryString && `&${queryString}`}
    `;
  },

  /**
   * Creates a URL with additional params object to add it to the trailing string for the service and passed params.
   *
   * @param category  The service category which can be media, auth... Etc
   * @param service   The service path
   * @param id  The id number to add it to the path before the query param
   * @param extraParams   list of extra params to add them to the trailing text of the search
   * @returns {string}
   */
  async buildUrlWithTrailingQuery(category, service, id, extraParams = {}) {
    const currentProfileData = await currentProfile.get.call(this);

    extraParams["platform"] = await this.getDefaultPlatform(extraParams.platform);
    extraParams["lang"] = localStorage.getItem("locale") || extraParams.lang || this.lang;
    // for legacy condition
    extraParams["parentalControl"] = currentProfileData
      ? currentProfileData.parentalControl
      : extraParams.parentalLevel || this.parentalLevel;
    // for MP each profile
    extraParams["parentalLevel"] = currentProfileData
      ? currentProfileData.parentalControl
      : extraParams.parentalLevel || this.parentalLevel;

    const queryString = this.parseQueryString(extraParams);

    return `${this.buildUrl(category, service)}${id ? id : ""}?${queryString}${this.getImageWidthQueryParams()}`;
  },

  /**
   * Creates a URL with additional params object to add it to the trailing string for the service and passed params and adds the asset types.
   *
   * @param category  The service category which can be media, auth... Etc
   * @param service   The service path
   * @param id  The id number to add it to the path before the query param
   * @param extraParams   list of extra params to add them to the trailing text of the search
   * @returns {string}
   */
  async buildUrlWithTrailingQueryAndAssetTypes(category, service, id, extraParams = {}) {
    const device = await this.device.getPlatform();
    const urlWithTrailingQuery = await this.buildUrlWithTrailingQuery(category, service, id, extraParams);
    return `${urlWithTrailingQuery}&mediaAssetTypes=${this.getMediaAssetTypes(device)}`;
  },

  /**
   * Creates a URL string for the reporting service.
   *
   * @example /reports/tizen2020?level=level&event=event&env=env
   *
   * @param origin
   * @param level
   * @param event
   * @param env
   * @param payload
   * @return {string}
   */
  buildUrlReport(origin, { level, event, env } = {}) {
    // TODO: Parametrize and/or specialize methods for different use cases. Better URL assembler
    return `${this.backendHosts.reports}${services.reports}/${origin}?level=${level}&event=${event}&env=${env}`;
  },

  /**
   * Creates a URL string for the sponsor ads
   *
   * @param adUnit  Ad URL
   * @param size  Ad Dimensions
   * @param config Extra Params
   * @return {string}
   */
  async buildSponsorAdUrl(adUnit, size, config) {
    const profileType = await profiletype.get(this);
    config["profileType"] = profileType;
    config["platform"] = this.clientType;
    config["lang"] = localStorage.getItem("locale") || config.lang || this.lang;
    const baseURL = services?.sponsorAd.general;
    const queryString = encodeURIComponent(this.parseQueryString(config));
    return `${baseURL}?iu=${adUnit}&sz=${size}&c="%%CACHEBUSTER%%"&t=${queryString}&d_imp=1`;
  },

  /**
   * Creates a URL string for the Video Commerce Data
   *
   * @param config "MediaGuid" picked from mediaList API
   * @return {string}
   */
  async buildVideoCommerceUrl(config) {
    const baseURL = services?.videoCommerce.general;
    const queryString = this.parseQueryString(config);
    return `${baseURL}?${queryString}`;
  },

  /**
   * Returns user JWT and/or x-profiletype x-geo-country in case it exists.
   */
  async getHeaders(isConcurrency = null) {
    // TODO : Optimise guest jwt handling
    const userToken =
      (this.jwtCookie && getCookieByName("guest-jwt")) ||
      (await token.getToken.call(this)) ||
      (this.jwtCookie && getCookieByName(this.jwtCookie.name));

    const deviceId = await this.device.getId();

    const profileType = !getTizenVersion() || isLowEndLG() ? "" : await profiletype.get(this);
    const country = geolocation.get()?.country || null;
    const currentProfileData = await currentProfile.get.call(this);
    const profileId = currentProfileData && currentProfileData.profileId;
    const userId = await localUserId.get.call(this);
    const profileCategory = await profile_category.get(this);

    if ((userToken && userToken !== "undefined") || profileType || country || isAvodLiveSupported) {
      return {
        headers: {
          ...(userToken && { Authorization: userToken }),
          ...(profileType && { "x-profiletype": profileType }),
          ...(profileId && { "x-profile-id": profileId }),
          ...(userId && { "x-user-id": userId }),
          ...(isConcurrency === true && deviceId && { "x-session-id": deviceId }),
          ...(profileCategory && { [this.multipleProfiles.headers.X_PROFILE_CATEGORY]: profileCategory })
        }
      };
    } else return null;
  },

  /**
   * Returns the selected profile JWT and/or x-profiletype x-geo-country in case it exists.
   */
  async getSelectedProfileHeaders(user = null, notInheartBeat = null) {
    let sendMasterProfileDataInHeaders = false;
    // TODO : Optimise guest jwt handling
    let userToken = (await currentProfile.getToken.call(this)) || (await token.getToken.call(this));

    if (user) {
      // to update someone else profile
      const { updateCache = true, profileJwt = "" } = user;
      if (!updateCache && profileJwt) {
        userToken = profileJwt;
      }
    }

    const profileType = !getTizenVersion() || isLowEndLG() ? "" : await profiletype.get(this);
    const country = geolocation.get()?.country || null;
    const profileId = await profile_id.get(this, sendMasterProfileDataInHeaders);
    const profileCategory = await profile_category.get(this, sendMasterProfileDataInHeaders);
    const userId = await localUserId.get.call(this);

    if ((userToken && userToken !== "undefined") || profileType || country || isAvodLiveSupported) {
      return {
        headers: {
          ...(userToken && { Authorization: userToken }),
          ...(profileType && { "x-profiletype": profileType }),
          ...(profileId && { [this.multipleProfiles.headers.X_PROFILE_ID]: profileId }),
          ...(userId && { "x-user-id": userId }),
          ...(notInheartBeat !== true &&
            profileCategory && { [this.multipleProfiles.headers.X_PROFILE_CATEGORY]: profileCategory })
        }
      };
    } else return null;
  },

  /**
   * Returns user SPX and/or x-profiletype x-geo-country in case it exists.
   */
  async getBackendApisHeaders() {
    let sendMasterProfileDataInHeaders = false;
    //  TODO : read spx profile then token
    // const userToken = getCookieByName("guest-spx")

    const guestProfile = this.guestProfie();
    const guestSpxToken = (guestProfile && guestProfile["guest-spx-token"]) || null;
    const currentProfileData = await currentProfile.get.call(this);

    let spxToken = null;
    let masterProfileSPXToken = await token.getSPXToken.call(this);

    if (guestSpxToken) {
      spxToken = guestSpxToken;
    } else if (currentProfileData?.isMasterProfile) {
      spxToken = masterProfileSPXToken;
    } else if (currentProfileData?.token) {
      spxToken = currentProfileData?.token;
    } else if (!currentProfileData) {
      spxToken = localStorage.getItem("currentProfileSPXToken") || null;
    }

    const profileType = await profiletype.get(this);
    const country = geolocation.get()?.country || null;
    const profileId = await profile_id.get(this, sendMasterProfileDataInHeaders);
    const profileCategory = await profile_category.get(this, sendMasterProfileDataInHeaders);

    if (profileType || country) {
      return {
        headers: {
          ...(spxToken && { Authorization: `Bearer ${spxToken}` }),
          ...(profileType && { "x-profiletype": profileType }),
          ...(country && { "x-geo-country": country.toUpperCase() }),
          ...(profileId && { [this.multipleProfiles.headers.X_PROFILE_ID]: profileId }),
          ...(profileCategory && { [this.multipleProfiles.headers.X_PROFILE_CATEGORY]: profileCategory })
        }
      };
    } else return null;
  },

  /**
   * Returns user SPX and/or x-profiletype x-geo-country in case it exists.
   */
  async getNMSApisHeaders() {
    let sendMasterProfileDataInHeaders = false;
    const profileType = await profiletype.get(this);
    const country = geolocation.get()?.country || null;
    const profileId = await profile_id.get(this, sendMasterProfileDataInHeaders);
    const profileCategory = await profile_category.get(this, sendMasterProfileDataInHeaders);
    const userId = await localUserId.get.call(this);

    if (profileType || country) {
      return {
        headers: {
          ...(profileType && { "x-profiletype": profileType }),
          ...(country && { "x-geo-country": country.toUpperCase() }),
          ...(profileId && { [this.multipleProfiles.headers.X_PROFILE_ID]: profileId }),
          ...(profileCategory && { [this.multipleProfiles.headers.X_PROFILE_CATEGORY]: profileCategory }),
          ...(userId && { "x-user-id": userId })
        }
      };
    } else return null;
  },

  async buildLayoutHeaders(xToken = null, xQuery = null, profileType = null, xgeoCountry = null) {
    let sendMasterProfileDataInHeaders = false;
    const userToken = await token.getToken.call(this); // No need to send currentProfile Header Because NMS APIs only need x-profile-id and x-user-id and x-profile-category data in headers
    const profileId = await profile_id.get(this, sendMasterProfileDataInHeaders);
    const profileCategory = await profile_category.get(this, sendMasterProfileDataInHeaders);
    const userId = await localUserId.get.call(this);

    const extraHeaders = {
      headers: {
        ...(!!userToken && { Authorization: userToken })
      }
    };
    !!xToken && (extraHeaders.headers["x-token"] = xToken);
    !!xQuery && (extraHeaders.headers["x-query"] = xQuery);
    !!profileType && (extraHeaders.headers["x-profiletype"] = profileType);
    !!xgeoCountry && (extraHeaders.headers["x-geo-country"] = xgeoCountry.toUpperCase());
    !!profileId && (extraHeaders.headers[this.multipleProfiles.headers.X_PROFILE_ID] = profileId);
    !!profileCategory && (extraHeaders.headers[this.multipleProfiles.headers.X_PROFILE_CATEGORY] = profileCategory);
    !!userId && (extraHeaders.headers["x-user-id"] = userId);

    return extraHeaders;
  },

  /**
   * Returns data type.
   */
  typeOf(obj) {
    return {}.toString
      .call(obj)
      .slice(8, -1)
      .toLowerCase();
  },

  formatErrorObject,

  handleErrorResponse(e) {
    throw formatErrorObject(e);
  },

  // FIXME: This is a temporary solution to fix cross domain errors on filmstrip and external subtitles requests
  parseNonSecureMediaContentUrl(url) {
    const urlList = url.split("/").filter(item => item !== "");
    const protocol = urlList[0];
    const hostname = urlList[1];
    return protocol === "http:" ? url.replace(`${protocol}//${hostname}`, externalDomainSecure) : url;
  },

  /**
   * @description Returns a closured scoped function that checks
   * whether the passed value is different from the previous one,
   * usually in order to avoid unnecessary processing.
   * Alternatively, a custom checking function can be chained to the default check.
   * @param initValue Optional initial value.
   * @param fnCheck Optional checking function, will be passed old and new values
   * and chained with the basic checking in a OR way.
   * @returns {Function} The scoped function.
   */
  retainValueFactory(initValue, fnCheck) {
    let oldValue = !!initValue || initValue === 0 ? initValue : null;
    return newVal => {
      if (newVal !== oldValue || (!!fnCheck && fnCheck(oldValue, newVal))) {
        oldValue = newVal;
        return true;
      } else {
        return false;
      }
    };
  },

  base64Enecode(data) {
    const encodedString = Base64.encode(data);
    return encodedString;
  },

  base64Decode(data) {
    const decodedString = Base64.decode(data);
    return decodedString;
  },

  guestProfie() {
    let guestProfile = getCookieByName("guest-profile");
    guestProfile = guestProfile && this.base64Decode(guestProfile);
    guestProfile = (guestProfile && JSON.parse(guestProfile)) || {};
    return guestProfile;
  },

  buildLinktvAdditionheaders(xGuid, xToken) {
    const extraHeaders = {
      headers: {
        ...(!!xGuid && { ["x-guid"]: xGuid }),
        ...(!!xToken && { ["x-token"]: xToken })
      }
    };
    return extraHeaders;
  }
};
